import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Control, useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanelLoader,
  SidePanelType,
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  PermissionEnum,
  Popup,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearClientsActivitiesFormState } from '../store/slices/client-activity-form';
import {
  useLazyGetClientsActivityQuery,
  useLazyGetClientsActivitiesListQuery,
  useCreateClientActivityMutation,
  useUpdateClientActivityMutation,
} from '../store/services';
import useRiskScore from '../../helpers/use-risk-score';

import ROUTES from '../../../routes';

interface FormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: string;
}

const ClientsActivitiesForm = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    name: '',
    riskScoreRangeId: '',
    riskScore: '',
  });

  const { item } = useAppSelector(
    (state) => state.individualClientsRisk.clientActivity.form,
  );
  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [getClientActivity, { isLoading, isFetching }] =
    useLazyGetClientsActivityQuery();
  const [getList] = useLazyGetClientsActivitiesListQuery();

  const [create, { isLoading: requestInProgress, isSuccess }] =
    useCreateClientActivityMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateSuccess }] =
    useUpdateClientActivityMutation();

  const { activityId } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: { name: '', riskScoreRangeId: '', riskScore: '' },
  });

  const riskScore = watch('riskScoreRangeId');
  const name = watch('name');
  const riskScoreNumber = watch('riskScore');

  const isDirty = !isEqual(defaultValues, {
    name,
    riskScoreRangeId: riskScore,
    riskScore: riskScoreNumber,
  });

  const { onFormSubmit, form, globalError } = useRiskScore({
    handleSubmit,
    itemId: activityId,
    setError,
    getList: (query: string) => getList(query),
    update,
    create,
    setValue,
    errors,
    control: control as Control<any>,
    nameTitle: 'Activity',
    disabled: Boolean(
      activityId &&
      !activePermissions?.includes(
        PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_EDIT_ACTION,
      ),
    ),
  });

  useEffect(() => {
    if (item) {
      reset({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item?.riskScoreRangeId,
      });

      setDefaultValues({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item?.riskScoreRangeId,
      });
    }
  }, [reset, item]);

  const getClientsActivitiesAction = useCallback(() => {
    getClientActivity(activityId as string);
  }, [activityId]);

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.INDIVIDUAL_CLIENTS_RISK_ACTIVITIES}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      setDefaultValues({
        name,
        riskScoreRangeId: riskScore,
        riskScore: riskScoreNumber,
      });

      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (activityId) {
      getClientsActivitiesAction();
    }

    return () => {
      dispatch(clearClientsActivitiesFormState());
    };
  }, [dispatch, activityId, getClientsActivitiesAction]);

  const isEditable = activityId
    ? activePermissions?.includes(
        PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_EDIT_ACTION,
      )
    : activePermissions?.includes(
        PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_CREATE_ACTION,
      );

  let content = form;
  const buttonText = activityId ? 'Save activity' : 'Add activity';
  let title;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !activityId &&
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  }

  if (activityId) {
    title = 'Edit activity';

    if (!isEditable) {
      title = 'Activity info';
    }
  } else {
    title = 'Add activity';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="client-activities"
        footer={
          <>
            {isEditable && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                disabled={!riskScore || !name}
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ClientsActivitiesForm;
